import React, { PureComponent, Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import {
  animateScroll as scroll,
  scroller,
} from 'react-scroll';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { isLoggedIn } from '../../api/Auth';
import {
  APIcreateFileComment,
  APIcreateShareFileComment,
  APIupdateFileComment,
  APIupdateShareFileComment,
  APIdeleteFileComment,
  APIdeleteShareFileComment,
  APIsubscribeFile,
  APIsubscribeShareFile,
  APIunsubscribeFile,
  APIunsubscribeShareFile,
} from '../../api/Files';

import { showConfirmationModal } from '../../actions/App';
import {
  fetchFileComments,
  fetchShareFileComments,
  setFileSubscription,
  setFileCommentsView,
  setFileCommentHighlighted,
  setFileCommentsOrder,
  setFileCommentsAnnotateMode,
  fetchFileVersionComments,
  fetchShareFileVersionComments,
} from '../../actions/Files';

import FileComment from './Comment';
import FileCommentForm from './CommentForm';
import FileSidebarEmptyState from './Sidebar/EmptyState';

import {
  getVisitorNameFromCookie,
  setVisitorNameCookie,
} from '../../helpers/Auth';
import { canSeeComments, defaultVideoCommentDuration } from '../../helpers/Comments';
import {
  getFileExtension,
  isAnnotationableImage,
} from '../../helpers/Files';
import {
  addFlashMessage,
  addFlashErrorMessage,
} from '../../helpers/FlashMessages';

import ErrorBoundary from '../ErrorBoundary';
import Loading from '../Loading';

class FileComments extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      name: getVisitorNameFromCookie(),
      commentMain: '',
      commentReply: '',
      commentEdit: '',
      commentDuration: defaultVideoCommentDuration,
      parentId: null,
      editId: null,
      scrollTo: null,
      initialScroll: null,
      initialActions: true,
      saving: false,
      deletingId: null,
      errors: {},
      highlightedComment: null,
      isPDF: false,
      isImage: false,
      isVideo: false,
    };

    this.timeoutID = null;

    this.commentInput = React.createRef();
    this.replyInput = React.createRef();
    this.editInput = React.createRef();

    this.isComponentMounted = false;

    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleCommentChange = this.handleCommentChange.bind(this);
    this.handleCommentDurationChange = this.handleCommentDurationChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handlePostComment = this.handlePostComment.bind(this);
    this.handleDeleteComment = this.handleDeleteComment.bind(this);
    this.handleChangeCommentsAnnotateMode = this.handleChangeCommentsAnnotateMode.bind(this);
    this.setScrollTo = this.setScrollTo.bind(this);
    this.setParent = this.setParent.bind(this);
    this.setEdit = this.setEdit.bind(this);
    this.switchSubscribed = this.switchSubscribed.bind(this);
    this.getFilteredComments = this.getFilteredComments.bind(this);
  }

  componentDidMount() {
    const { file } = this.props;
    this.isComponentMounted = true;
    this.setInitialScroll();

    const fileExtension = getFileExtension(file);

    if (['PDF'].includes(fileExtension)) {
      this.setState({ isPDF: true });
    } else if (file.type_group === 'video') {
      this.setState({ isVideo: true });
    } else if (isAnnotationableImage(file)) {
      this.setState({ isImage: true });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.isComponentMounted) {
      return;
    }

    const { file: prevFile } = prevProps;
    const { file, fileVersion, commentHighlighted } = this.props;
    if (file.uuid !== prevFile.uuid ||
      fileVersion !== prevProps.fileVersion ||
      file.versions.length !== prevProps.file.versions.length) {
      this.loadComments();
    }

    const { scrollTo: prevScrollTo } = prevState;
    const { scrollTo } = this.state;
    if (scrollTo && prevScrollTo !== scrollTo) {
      this.scroll();
    } else if (commentHighlighted) {
      this.setState({ scrollTo: `comment-${commentHighlighted.commentId}` });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.highlightTimer);
    this.isComponentMounted = false;
  }

  addHighlightedComment = (id) => {
    const { highlightedComment } = this.state;
    const useId = parseInt(id);

    if (highlightedComment !== useId) {
      this.setState({
        highlightedComment: useId,
      });

      clearTimeout(this.timeoutID);

      this.timeoutID = setTimeout(() => {
        this.removeHighlightedComment();
      }, 5000);
    } else {
      clearTimeout(this.timeoutID);
      this.timeoutID = setTimeout(() => {
        this.removeHighlightedComment();
      }, 5000);
    }
  };

  removeHighlightedComment = () => {
    this.setState({
      highlightedComment: null,
    });
  };

  setInitialScroll() {
    const hash = window.location.hash.replace('#', '');
    const hashParts = hash.split('-');
    if (hashParts[0] === 'comment' && hashParts[1]) {
      this.setState({ initialScroll: hash }, () => {
        this.addHighlightedComment(hashParts[1]);
        this.loadComments();
      });
    } else {
      this.loadComments();
    }
  }

  setScrollTo(scrollTo) {
    this.setState({ scrollTo });
  }

  setParent(parentId) {
    this.setState((prevState) => ({
      parentId,
      editId: null,
      commentEdit: '',
      commentReply: parentId ? prevState.commentReply : '',
    }), () => {
      if (parentId) {
        this.replyInput.focus();
      } else {
        this.commentInput.focus();
      }
    });
  }

  findCommentById(commentId) {
    const { comments } = this.props;

    const topLevel = comments.find(c => c.id === commentId);
    if (topLevel) {
      return topLevel;
    }

    for (let i = 0; i < comments.length; i += 1) {
      const reply = comments[i].replies.find(r => r.id === commentId);
      if (reply) {
        return reply;
      }
    }

    return null;
  }

  setEdit(editId) {
    const commentToEdit = editId ? this.findCommentById(editId) : null;

    this.setState({
      editId,
      parentId: null,
      commentReply: '',
      commentEdit: commentToEdit?.comment || '',
    }, () => {
      if (editId) {
        this.editInput.focus();
      }
    });
  }

  doActionsFromUrl() {
    const { commentsSubscribed } = this.props;
    const { initialActions } = this.state;
    if (!initialActions) {
      return;
    }

    const hash = window.location.hash.replace('#', '');
    if (hash === 'subscribe' && !commentsSubscribed) {
      this.subscribe();
      this.setState({ initialActions: false });
    }
    if (hash === 'unsubscribe' && commentsSubscribed) {
      this.unsubscribe();
      this.setState({ initialActions: false });
    }
  }

  scroll() {
    const { scrollTo } = this.state;
    const { highlightComment } = this.props;

    if (scrollTo) {
      if (highlightComment) highlightComment(null);
      const idParts = scrollTo.split('-');
      if (idParts[1]) {
        this.addHighlightedComment(idParts[1]);
      }

      // const element = document.getElementById(scrollTo);
      const container = document.getElementById('file-comments');
      const commentForm = document.getElementById('file-comment-form');

      if (!commentForm) return;

      scroller.scrollTo(scrollTo, {
        containerId: 'file-comments',
        duration: 500,
        smooth: true,
        offset: (window.innerHeight - commentForm.clientHeight - container.clientHeight + 20) * -1,
      });
      this.setState({ scrollTo: null });
    } else {
      scroll.scrollToBottom({
        containerId: 'file-comments',
      });
    }
  }

  loadComments(silent = false) {
    const {
      file,
      fileVersion,
      loadComments,
      loadShareComments,
      share,
      updateCommentsCount,
    } = this.props;

    if (!this.isComponentMounted) {
      return false;
    }

    if (!canSeeComments()) {
      return false;
    }

    if (share) {
      if (!share.allow_comments) {
        return false;
      }

      return loadShareComments(share.uuid, file.uuid, fileVersion, silent).then(() => {
        const { initialScroll } = this.state;
        this.setScrollTo(initialScroll);
        if (initialScroll) {
          this.setState({
            scrollTo: initialScroll,
            initialScroll: null,
          });
        }
        this.doActionsFromUrl();
      });
    }

    return loadComments(file.uuid, fileVersion, silent).then((commentsResponse) => {
      const { initialScroll } = this.state;
      if (initialScroll) {
        this.setState({
          scrollTo: initialScroll,
          initialScroll: null,
        });
      }
      if (commentsResponse) {
        updateCommentsCount(commentsResponse.comments_count, fileVersion);
      }
      this.doActionsFromUrl();
    });
  }

  subscribe() {
    const {
      intl,
      file,
      share,
    } = this.props;

    if (share) {
      APIsubscribeShareFile(share.uuid, file.uuid).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Notifications about new comments are now turned on' }),
        });
        this.loadComments(true);
      });
    } else {
      APIsubscribeFile(file.uuid).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Notifications about new comments are now turned on' }),
        });
        this.loadComments(true);
      });
    }
  }

  unsubscribe() {
    const {
      intl,
      file,
      share,
    } = this.props;

    if (share) {
      APIunsubscribeShareFile(share.uuid, file.uuid).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Notifications about new comments are now muted' }),
        });
        this.loadComments(true);
      });
    } else {
      APIunsubscribeFile(file.uuid).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Notifications about new comments are now muted' }),
        });
        this.loadComments(true);
      });
    }
  }

  switchSubscribed() {
    const {
      commentsSubscribed,
      fileSubscription,
    } = this.props;

    if (commentsSubscribed) {
      this.unsubscribe();
      fileSubscription(false);
    } else {
      this.subscribe();
      fileSubscription(true);
    }
  }

  updateComment() {
    const {
      intl,
      comments,
      file,
      fileVersion,
      loadComments,
      loadShareComments,
      share,
      updateCommentsCount,
    } = this.props;
    const {
      commentEdit,
      commentDuration,
      editId,
    } = this.state;

    const commentInfo = {
      id: editId,
    };

    if (commentEdit.length > 0) {
      commentInfo.comment = commentEdit;
    }

    const meta = comments.find(c => c.id === editId);

    if (!_.isNull(meta) && _.isNumber(Number(commentDuration))) {
      commentInfo.meta = {
        ...meta.meta,
        duration: commentDuration,
      };
    }

    if (share) {
      APIupdateShareFileComment(share.uuid, file.uuid, commentInfo).then((response) => {
        const { data } = response;
        loadShareComments(share.uuid, file.uuid, fileVersion, true).then(() => {
          this.editInput.value = '';
          this.setState({
            commentEdit: '',
            scrollTo: `comment-${data.id}`,
            editId: null,
            saving: false,
          });
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your changes have been saved' }),
          });
        });
      }).catch(() => {
        addFlashErrorMessage(intl);
        this.setState({ saving: false });
      });
    } else {
      APIupdateFileComment(commentInfo).then((response) => {
        const { data } = response;
        loadComments(file.uuid, fileVersion, true).then((commentsResponse) => {
          this.editInput.value = '';
          this.setState({
            commentEdit: '',
            scrollTo: `comment-${data.id}`,
            editId: null,
            saving: false,
          });
          updateCommentsCount(commentsResponse.comments_count, fileVersion);
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your changes have been saved' }),
          });
        });
      }).catch(() => {
        addFlashErrorMessage(intl);
        this.setState({ saving: false });
      });
    }
  }

  createComment() {
    const {
      intl,
      file,
      fileVersion,
      loadComments,
      loadShareComments,
      share,
      updateCommentsCount,
    } = this.props;
    const {
      name,
      commentMain,
      commentReply,
      parentId,
    } = this.state;
    const comment = parentId ? commentReply : commentMain;

    if (share) {
      APIcreateShareFileComment(share.uuid, file.uuid, {
        name,
        comment,
        parent_id: parentId,
        file_version_id: fileVersion,
      }).then((response) => {
        const { data } = response;
        loadShareComments(share.uuid, file.uuid, fileVersion, true).then(() => {
          this.setState({
            commentMain: parentId ? commentMain : '',
            commentReply: parentId ? '' : commentReply,
            scrollTo: `comment-${data.id}`,
            parentId: null,
            saving: false,
          }, () => {
            this.commentInput.value = '';
            if (parentId && this.replyInput) {
              this.replyInput.value = '';
            }
          });
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your comment has been added' }),
          });
        });
      }).catch(() => {
        addFlashErrorMessage(intl);
        this.setState({ saving: false });
      });
    } else {
      APIcreateFileComment(file.uuid, {
        comment,
        parent_id: parentId,
        file_version_id: fileVersion,
      }).then((response) => {
        const { data } = response;
        loadComments(file.uuid, fileVersion, true).then((commentsResponse) => {
          this.commentInput.value = '';
          if (parentId && this.replyInput) {
            this.replyInput.value = '';
          }
          this.setState({
            commentMain: parentId ? commentMain : '',
            commentReply: parentId ? '' : commentReply,
            scrollTo: `comment-${data.id}`,
            parentId: null,
            saving: false,
          });
          updateCommentsCount(commentsResponse.comments_count, fileVersion);
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your comment has been added' }),
          });
        });
      }).catch(() => {
        addFlashErrorMessage(intl);
        this.setState({ saving: false });
      });
    }
  }

  handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handlePostComment();
    }
  }

  handlePostComment() {
    const {
      name,
      commentMain,
      commentReply,
      commentEdit,
      editId,
      parentId,
    } = this.state;
    let comment = commentMain;

    if (parentId) {
      comment = commentReply;
    }

    if (editId) {
      comment = commentEdit;
    }

    const errors = {};
    if (!isLoggedIn() && (!name || name.length === 0)) {
      errors.name = true;
    }
    if (comment.length === 0 && !editId) {
      errors.comment = true;
    }
    this.setState({ errors });
    if (Object.keys(errors).length > 0) {
      return;
    }

    this.setState({ saving: true });

    if (name) {
      setVisitorNameCookie(name);
    }

    if (editId) {
      this.updateComment();
    } else {
      this.createComment();
    }
  }

  handleDeleteComment(commentId) {
    const { intl, openConfirmationModal } = this.props;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Do you want to delete this comment?' }),
      onConfirm: () => (
        this.deleteComment(commentId)
      ),
    };
    openConfirmationModal(modalParams);
  }

  deleteComment(commentId) {
    const {
      intl,
      file,
      fileVersion,
      loadComments,
      loadShareComments,
      share,
      updateCommentsCount,
    } = this.props;

    this.setState({ deletingId: commentId });

    if (share) {
      return APIdeleteShareFileComment(share.uuid, file.uuid, commentId).then(() => (
        loadShareComments(share.uuid, file.uuid, fileVersion, true).then(() => {
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'The comment has been deleted' }),
          });
          this.setState({ deletingId: null });
        })
      )).catch(() => {
        addFlashErrorMessage(intl);
        this.setState({ deletingId: null });
      });
    }

    return APIdeleteFileComment(commentId).then(() => (
      loadComments(file.uuid, fileVersion, true).then((commentsResponse) => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The comment has been deleted' }),
        });
        this.setState({ deletingId: null });
        updateCommentsCount(commentsResponse.comments_count, fileVersion);
      })
    )).catch(() => {
      addFlashErrorMessage(intl);
      this.setState({ deletingId: null });
    });
  }

  handleNameChange(e) {
    const { value } = e.target;
    this.setState({ name: value });
  }

  handleCommentChange(commentValue) {
    this.setState((prevState) => {
      if (prevState.editId) {
        return { commentEdit: commentValue };
      }

      if (prevState.parentId) {
        return { commentReply: commentValue };
      }

      return { commentMain: commentValue };
    });
  }

  handleCommentDurationChange(e) {
    const { value } = e.target;
    this.setState({ commentDuration: value });
  }

  handleChangeCommentsAnnotateMode() {
    const { changeCommentsAnnotateMode, commentsAnnotateMode } = this.props;
    const {
      isPDF,
      isImage,
      isVideo,
    } = this.state;

    const allowAnnotations = isPDF || isImage || isVideo;

    if (!allowAnnotations) return;

    changeCommentsAnnotateMode(!commentsAnnotateMode);
  }

  getFilteredComments() {
    const {
      comments,
      commentsView,
      commentsOrder,
    } = this.props;
    const {
      isPDF,
      isImage,
      isVideo,
    } = this.state;
    if (commentsView === 'all') return comments;

    if (commentsView === 'in-file') {
      let filtered = [];
      if (isPDF) {
        filtered = comments.filter(c => (c.type === 'pdf'));
      } else if (isImage) {
        filtered = comments.filter(c => (c.type === 'image'));
      } else if (isVideo) {
        filtered = comments.filter(c => (c.type === 'video'));
      }

      if (!commentsOrder) return filtered;

      if (isPDF) {
        const getBoundingRect = (comment) => comment.meta.position.boundingRect;
        const getPage = (comment) => comment.meta.position.pageNumber;

        const sorted = filtered.sort((a, b) => {
          if (!a.meta || !b.meta) return 0;
          const aRect = getBoundingRect(a);
          const bRect = getBoundingRect(b);

          const aPage = Number(getPage(a));
          const bPage = Number(getPage(b));

          if (aPage !== bPage) {
            return aPage - bPage;
          }
          if (Math.max(aRect.y1, aRect.y2) === Math.max(bRect.y1, bRect.y2)) {
            if (Math.max(aRect.x1, aRect.x2) === Math.max(bRect.x1, bRect.x2)) return 0;
            return Math.max(aRect.x1, aRect.x2) - Math.max(bRect.x1, bRect.x2);
          }
          return Math.max(aRect.y1, aRect.y2) - Math.max(bRect.y1, bRect.y2);
        });

        return sorted;
      } if (isImage) {
        const sorted = filtered.sort((a, b) => {
          const xA = a.meta.x;
          const xB = b.meta.x;
          const yA = a.meta.y;
          const yB = b.meta.y;

          if (yA === yB) return xA - xB;
          return yA - yB;
        });

        return sorted;
      }
      return filtered;
    }

    if (commentsView === 'other') {
      return comments.filter(c => c.type === null);
    }

    return [];
  }

  render() {
    const {
      intl,
      commentsSubscribed,
      commentsLoading,
      commentsView,
      commentsOrder,
      file,
      changeCommentsView,
      changeCommentsOrder,
      show,
      handleSidebarTab,
      readOnly,
    } = this.props;
    const {
      commentMain,
      commentReply,
      commentEdit,
      parentId,
      editId,
      name,
      saving,
      deletingId,
      errors,
      highlightedComment,
      isPDF,
      isImage,
      isVideo,
    } = this.state;

    const allowAnnotations = isPDF || isImage || isVideo;
    const filteredComments = this.getFilteredComments();

    if (!show) return null;

    return (
      <div className="file-comments file-details-item">
        <div className="file-detail-header">
          <span>
            {commentsView === 'all' && intl.formatMessage({ defaultMessage: 'All comments' })}
            {commentsView === 'in-file' && intl.formatMessage({ defaultMessage: 'Annotations' })}
            {commentsView === 'other' && intl.formatMessage({ defaultMessage: 'Comments' })}
            {` (${filteredComments.length})`}
          </span>
          <div className="file-detail-header-buttons">
            {(allowAnnotations && commentsView === 'in-file') && (
              <div className="dropdown-comments-filters">
                <UncontrolledDropdown>
                  <DropdownToggle
                    size="sm"
                    className={classnames('dropdown-comments-filters-button', { active: commentsOrder !== null })}
                  >
                    <i className="far fa-arrow-up-arrow-down" />
                  </DropdownToggle>
                  <DropdownMenu container="body">
                    <DropdownItem
                      active={commentsOrder === null}
                      onClick={() => changeCommentsOrder(null)}
                    >
                      {intl.formatMessage({ defaultMessage: 'Default' })}
                    </DropdownItem>
                    <DropdownItem
                      active={commentsOrder === 'file-order'}
                      onClick={() => changeCommentsOrder('file-order')}
                    >
                      {intl.formatMessage({ defaultMessage: 'File order' })}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            )}
            {allowAnnotations && (
              <div className="dropdown-comments-filters">
                <UncontrolledDropdown>
                  <DropdownToggle
                    size="sm"
                    className={classnames({ active: commentsView !== 'all' })}
                  >
                    {intl.formatMessage({ defaultMessage: 'Filter' })}
                  </DropdownToggle>
                  <DropdownMenu container="body">
                    <DropdownItem
                      active={commentsView === 'all'}
                      onClick={() => {
                        changeCommentsView('all');
                        changeCommentsOrder(null);
                      }}
                    >
                      {intl.formatMessage({ defaultMessage: 'All' })}
                    </DropdownItem>
                    <DropdownItem
                      active={commentsView === 'in-file'}
                      onClick={() => changeCommentsView('in-file')}
                    >
                      {intl.formatMessage({ defaultMessage: 'Annotations' })}
                    </DropdownItem>
                    <DropdownItem
                      active={commentsView === 'other'}
                      onClick={() => {
                        changeCommentsView('other');
                        changeCommentsOrder(null);
                      }}
                    >
                      {intl.formatMessage({ defaultMessage: 'Comments' })}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            )}
            {(isLoggedIn() && !commentsLoading) && (
              <>
                <Button
                  color="link"
                  onClick={this.switchSubscribed}
                  className="file-detail-comments-notifications"
                  id="FileDetailCommentsNotifications"
                  size="sm"
                >
                  {commentsSubscribed ? (
                    <i className="fal fa-bell" />
                  ) : (
                    <i className="fal fa-bell-slash" />
                  )}
                </Button>
                <UncontrolledTooltip placement="bottom" target="FileDetailCommentsNotifications">
                  {commentsSubscribed ? intl.formatMessage({ defaultMessage: 'Mute notifications' }) :
                    intl.formatMessage({ defaultMessage: 'Turn on notifications' })}
                </UncontrolledTooltip>
              </>
            )}
          </div>
        </div>

        {commentsLoading ? (
          <Loading />
        ) : (
          <ErrorBoundary>
            <div id="file-comments" className="file-detail-body file-comments-body">
              {(filteredComments.length === 0 && (commentsView === null || commentsView === 'all')) && (
                <FileSidebarEmptyState
                  info={intl.formatMessage({ defaultMessage: 'No comments yet' })}
                  wiki="https://help.stockpress.co/en/articles/5937476-leaving-comments-and-annotations-on-images-videos-and-pdfs"
                  video="https://player.vimeo.com/video/1063863507?h=79b83d38ec&badge=0&byline=0&title=0"
                  title={intl.formatMessage({ defaultMessage: 'File annotations' })}
                />
              )}
              {(filteredComments.length === 0 && commentsView === 'in-file') && (
                <FileSidebarEmptyState
                  info={intl.formatMessage({ defaultMessage: 'No annotations on file' })}
                  wiki="https://help.stockpress.co/en/articles/5937476-leaving-comments-and-annotations-on-images-videos-and-pdfs"
                  video="https://player.vimeo.com/video/1063863507?h=79b83d38ec&badge=0&byline=0&title=0"
                  title={intl.formatMessage({ defaultMessage: 'File annotations' })}
                />
              )}
              {(filteredComments.length === 0 && commentsView === 'other') && (
                <FileSidebarEmptyState
                  info={intl.formatMessage({ defaultMessage: 'No other comments' })}
                  wiki="https://help.stockpress.co/en/articles/5937476-leaving-comments-and-annotations-on-images-videos-and-pdfs"
                  video="https://player.vimeo.com/video/1063863507?h=79b83d38ec&badge=0&byline=0&title=0"
                  title={intl.formatMessage({ defaultMessage: 'File annotations' })}
                />
              )}
              {filteredComments.map(c => (
                <Fragment key={c.id}>
                  <FileComment
                    comment={c}
                    commentState={editId === c.id ? commentEdit : ''}
                    isEdit={editId === c.id}
                    setScrollTo={this.setScrollTo}
                    setParent={this.setParent}
                    setEdit={this.setEdit}
                    saving={saving}
                    deleting={deletingId === c.id}
                    highlighted={highlightedComment === c.id}
                    inputRefFunc={(ref) => { this.editInput = ref; }}
                    handleKeyPress={this.handleKeyPress}
                    handleCommentChange={this.handleCommentChange}
                    handleCommentDurationChange={this.handleCommentDurationChange}
                    handlePostComment={this.handlePostComment}
                    handleDeleteComment={this.handleDeleteComment}
                    handleSidebarTab={handleSidebarTab}
                    readOnly={readOnly}
                  />
                  {c.replies.map(r => (
                    <FileComment
                      key={r.id}
                      comment={r}
                      isReply={true}
                      commentState={editId === r.id ? commentEdit : ''}
                      isEdit={editId === r.id}
                      setScrollTo={this.setScrollTo}
                      setParent={this.setParent}
                      setEdit={this.setEdit}
                      saving={saving}
                      deleting={deletingId === r.id}
                      highlighted={highlightedComment === c.id}
                      inputRefFunc={(ref) => { this.editInput = ref; }}
                      handleKeyPress={this.handleKeyPress}
                      handleCommentChange={this.handleCommentChange}
                      handlePostComment={this.handlePostComment}
                      handleDeleteComment={this.handleDeleteComment}
                      handleSidebarTab={handleSidebarTab}
                      readOnly={readOnly}
                    />
                  ))}
                  {parentId === c.id && (
                    <FileCommentForm
                      isReply={true}
                      commentValue={commentReply}
                      name={name}
                      saving={saving}
                      errors={errors}
                      inputRefFunc={(ref) => { this.replyInput = ref; }}
                      handleKeyPress={this.handleKeyPress}
                      handleNameChange={this.handleNameChange}
                      handleCommentChange={this.handleCommentChange}
                      handlePostComment={this.handlePostComment}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </ErrorBoundary>
        )}

        {(!readOnly && !commentsLoading) && (
          <div id="file-comment-form" className="file-detail-footer file-add-comment">
            <FileCommentForm
              commentValue={commentMain}
              name={name}
              saving={saving}
              errors={errors}
              inputRefFunc={(ref) => { this.commentInput = ref; }}
              handleKeyPress={this.handleKeyPress}
              handleNameChange={this.handleNameChange}
              handleCommentChange={this.handleCommentChange}
              handlePostComment={() => {
                this.setState({ parentId: null }, () => {
                  this.handlePostComment();
                });
              }}
              fileExtension={getFileExtension(file)}
            />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  comments: state.Files.comments,
  commentsSubscribed: state.Files.commentsSubscribed,
  commentsLoading: state.Files.commentsLoading,
  commentsView: state.Files.commentsView,
  commentsOrder: state.Files.commentsOrder,
  commentHighlighted: state.Files.commentHighlighted,
  commentsAnnotateMode: state.Files.commentsAnnotateMode,
  share: state.Shares.share,
});

const mapDispatchToProps = dispatch => ({
  loadComments: (fileUuid, fileVersion, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchFileVersionComments(fileUuid, fileVersion, silent));
    }
    return dispatch(fetchFileComments(fileUuid, silent));
  },
  loadShareComments: (shareUuid, fileUuid, fileVersion, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchShareFileVersionComments(shareUuid, fileUuid, fileVersion, silent));
    }
    return dispatch(fetchShareFileComments(shareUuid, fileUuid, silent));
  },
  changeCommentsView: (view) => (
    dispatch(setFileCommentsView(view))
  ),
  changeCommentsOrder: (order) => (
    dispatch(setFileCommentsOrder(order))
  ),
  changeCommentsAnnotateMode: (commentsAnnotateMode) => (
    dispatch(setFileCommentsAnnotateMode(commentsAnnotateMode))
  ),
  fileSubscription: status => (
    dispatch(setFileSubscription(status))
  ),
  openConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
  highlightComment: (comment) => {
    dispatch(setFileCommentHighlighted(comment));
  },
});

FileComments.propTypes = {
  intl: PropTypes.object.isRequired,
  loadComments: PropTypes.func.isRequired,
  loadShareComments: PropTypes.func.isRequired,
  changeCommentsView: PropTypes.func.isRequired,
  changeCommentsOrder: PropTypes.func.isRequired,
  changeCommentsAnnotateMode: PropTypes.func.isRequired,
  fileSubscription: PropTypes.func.isRequired,
  openConfirmationModal: PropTypes.func.isRequired,
  file: PropTypes.object.isRequired,
  fileVersion: PropTypes.number,
  comments: PropTypes.array.isRequired,
  commentsSubscribed: PropTypes.bool.isRequired,
  commentsLoading: PropTypes.bool.isRequired,
  commentsView: PropTypes.string.isRequired,
  commentsOrder: PropTypes.string,
  commentHighlighted: PropTypes.object,
  commentsAnnotateMode: PropTypes.bool.isRequired,
  show: PropTypes.bool.isRequired,
  share: PropTypes.object,
  highlightComment: PropTypes.func.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
  handleSidebarTab: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

FileComments.defaultProps = {
  share: null,
  commentHighlighted: null,
  commentsOrder: null,
  fileVersion: null,
  readOnly: false,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(FileComments));
